import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, CardHeader,
  Button, TextField, IconButton, Chip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Switch, FormControlLabel, Alert, Tooltip,
  Snackbar, CircularProgress, Badge, InputAdornment,
  Accordion, AccordionSummary, AccordionDetails,
  List, ListItem, ListItemText, ListItemSecondaryAction,
} from '@mui/material';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  LockOutlined as LockIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Discount as DiscountIcon,
  CreditCard as CreditCardIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import axios from 'axios';
import API_BASE from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { formatScanFrequency } from '../../utils/scanFrequencyLabels';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PlanConfig {
  id: string;
  plan_key: string;
  display_name: string;
  tagline: string | null;
  is_popular: boolean;
  display_order: number;
  price_monthly: number;
  price_annually_monthly: number;
  price_annually_total: number;
  annual_discount_pct: number;
  annual_savings: number;
  trial_days: number;
  max_employees: number;
  scan_frequency: string;
  features: string[];
  autofix_tiers: number[];
  compliance_frameworks: string[];
  is_active: boolean;
  updated_at: string | null;
  stripe_price_id_monthly?: string;
  stripe_price_id_annually?: string;
}

interface EditState {
  // Price fields — super_admin only
  price_monthly_cents?: number;
  annual_discount_pct?: number;
  trial_days?: number;
  stripe_price_id_monthly?: string;
  stripe_price_id_annually?: string;
  // Shared fields
  display_name?: string;
  tagline?: string;
  is_popular?: boolean;
  display_order?: number;
  max_employees?: number;
  scan_frequency?: string;
  features?: string[];
  autofix_tiers?: number[];
  compliance_frameworks?: string[];
  is_active?: boolean;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const SCAN_FREQUENCIES = ['daily', 'twice_daily', 'every_6_hours'];

function FeatureEditor({
  features,
  onChange,
}: {
  features: string[];
  onChange: (f: string[]) => void;
}) {
  const [newFeature, setNewFeature] = useState('');

  const add = () => {
    const trimmed = newFeature.trim();
    if (!trimmed) return;
    onChange([...features, trimmed]);
    setNewFeature('');
  };

  const remove = (idx: number) => onChange(features.filter((_, i) => i !== idx));

  return (
    <Box>
      <List dense disablePadding>
        {features.map((f, i) => (
          <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
            <ListItemText
              primary={f}
              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
            />
            <ListItemSecondaryAction>
              <IconButton size="small" onClick={() => remove(i)} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Add feature…"
          value={newFeature}
          onChange={e => setNewFeature(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()}
        />
        <IconButton onClick={add} color="primary" size="small">
          <AddIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

function PlanCard({
  plan,
  canEditPrices,
  onSave,
  onToggleActive,
}: {
  plan: PlanConfig;
  canEditPrices: boolean;
  onSave: (planKey: string, data: EditState) => Promise<void>;
  onToggleActive: (planKey: string, active: boolean) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<EditState>({});

  const startEdit = () => {
    setDraft({
      price_monthly_cents: Math.round(plan.price_monthly * 100),
      annual_discount_pct: plan.annual_discount_pct,
      trial_days: plan.trial_days ?? 14,
      stripe_price_id_monthly: plan.stripe_price_id_monthly || '',
      stripe_price_id_annually: plan.stripe_price_id_annually || '',
      display_name: plan.display_name,
      tagline: plan.tagline || '',
      is_popular: plan.is_popular,
      display_order: plan.display_order,
      max_employees: plan.max_employees,
      scan_frequency: plan.scan_frequency,
      features: [...plan.features],
      compliance_frameworks: [...plan.compliance_frameworks],
    });
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraft({});
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(plan.plan_key, draft);
      setEditing(false);
      setDraft({});
    } finally {
      setSaving(false);
    }
  };

  const set = (key: keyof EditState, value: any) =>
    setDraft(prev => ({ ...prev, [key]: value }));

  const annualPricePreview =
    draft.price_monthly_cents && draft.annual_discount_pct !== undefined
      ? ((draft.price_monthly_cents / 100) *
          (1 - (draft.annual_discount_pct ?? 0) / 100)).toFixed(2)
      : plan.price_annually_monthly.toFixed(2);

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: plan.is_popular ? 'primary.main' : 'divider',
        borderWidth: plan.is_popular ? 2 : 1,
        position: 'relative',
        opacity: plan.is_active ? 1 : 0.6,
        transition: 'opacity 0.2s',
      }}
    >
      {plan.is_popular && (
        <Chip
          label="MOST POPULAR"
          color="primary"
          size="small"
          sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)' }}
        />
      )}

      <CardHeader
        title={
          editing ? (
            <TextField
              size="small"
              value={draft.display_name}
              onChange={e => set('display_name', e.target.value)}
              sx={{ width: 180 }}
            />
          ) : (
            <Typography variant="h6" fontWeight={700}>
              {plan.display_name}
            </Typography>
          )
        }
        subheader={
          editing ? (
            <TextField
              size="small"
              fullWidth
              value={draft.tagline}
              onChange={e => set('tagline', e.target.value)}
              placeholder="Tagline…"
              sx={{ mt: 0.5 }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {plan.tagline}
            </Typography>
          )
        }
        action={
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={plan.is_active}
                  onChange={e => onToggleActive(plan.plan_key, e.target.checked)}
                  color={plan.is_active ? 'success' : 'default'}
                />
              }
              label={
                <Typography variant="caption">{plan.is_active ? 'Active' : 'Inactive'}</Typography>
              }
              labelPlacement="start"
            />
            {editing ? (
              <>
                <IconButton size="small" color="success" onClick={save} disabled={saving}>
                  {saving ? <CircularProgress size={16} /> : <CheckIcon />}
                </IconButton>
                <IconButton size="small" color="error" onClick={cancel}>
                  <CloseIcon />
                </IconButton>
              </>
            ) : (
              <IconButton size="small" onClick={startEdit} color="primary">
                <EditIcon />
              </IconButton>
            )}
          </Box>
        }
      />

      <CardContent sx={{ pt: 0 }}>
        <Divider sx={{ mb: 2 }} />

        {/* Pricing block */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
            <CreditCardIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
            Pricing
          </Typography>

          {editing && canEditPrices ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                label="Monthly (cents)"
                type="number"
                size="small"
                value={draft.price_monthly_cents}
                onChange={e => set('price_monthly_cents', parseInt(e.target.value) || 0)}
                helperText={`$${((draft.price_monthly_cents || 0) / 100).toFixed(2)}/mo`}
                sx={{ width: 150 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">¢</InputAdornment>,
                }}
              />
              <TextField
                label="Annual discount %"
                type="number"
                size="small"
                value={draft.annual_discount_pct}
                onChange={e => set('annual_discount_pct', parseFloat(e.target.value) || 0)}
                helperText={`→ $${annualPricePreview}/mo`}
                sx={{ width: 150 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Box>
          ) : editing && !canEditPrices ? (
            <Alert severity="info" icon={<LockIcon />} sx={{ py: 0.5 }}>
              <Typography variant="caption">Prices are locked — contact super admin</Typography>
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'baseline' }}>
              <Box>
                <Typography variant="h5" fontWeight={800} color="primary.main">
                  ${plan.price_monthly.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / month
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1" fontWeight={600} color="success.main">
                  ${plan.price_annually_monthly.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / mo (annual — {plan.annual_discount_pct}% off)
                </Typography>
              </Box>
              <Chip
                label={`Save $${plan.annual_savings.toFixed(0)}/yr`}
                color="success"
                size="small"
                variant="outlined"
              />
            </Box>
          )}

          {/* Stripe IDs — super_admin only */}
          {canEditPrices && editing && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <TextField
                label="Stripe Monthly Price ID"
                size="small"
                value={draft.stripe_price_id_monthly}
                onChange={e => set('stripe_price_id_monthly', e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
                placeholder="price_..."
              />
              <TextField
                label="Stripe Annual Price ID"
                size="small"
                value={draft.stripe_price_id_annually}
                onChange={e => set('stripe_price_id_annually', e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
                placeholder="price_..."
              />
            </Box>
          )}
          {canEditPrices && !editing && (
            <Box sx={{ mt: 0.5, display: 'flex', gap: 1 }}>
              {plan.stripe_price_id_monthly && (
                <Chip label={`M: ${plan.stripe_price_id_monthly}`} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 10 }} />
              )}
              {plan.stripe_price_id_annually && (
                <Chip label={`A: ${plan.stripe_price_id_annually}`} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 10 }} />
              )}
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Limits */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
            <GroupIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
            Limits
          </Typography>
          {editing ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                label="Max employees"
                type="number"
                size="small"
                value={draft.max_employees}
                onChange={e => set('max_employees', parseInt(e.target.value) || 1)}
                sx={{ width: 140 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">users</InputAdornment>,
                }}
              />
              <TextField
                select
                label="Scan frequency"
                size="small"
                value={draft.scan_frequency}
                onChange={e => set('scan_frequency', e.target.value)}
                sx={{ width: 140 }}
                SelectProps={{ native: true }}
              >
                {SCAN_FREQUENCIES.map(f => (
                  <option key={f} value={f}>{formatScanFrequency(f)}</option>
                ))}
              </TextField>
              <TextField
                label="Free trial days"
                type="number"
                size="small"
                value={draft.trial_days}
                onChange={e => set('trial_days', parseInt(e.target.value) || 0)}
                sx={{ width: 140 }}
                InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={draft.is_popular}
                    onChange={e => set('is_popular', e.target.checked)}
                  />
                }
                label={<Typography variant="caption">Most popular</Typography>}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Chip
                icon={<GroupIcon />}
                label={`Up to ${plan.max_employees} employees`}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={<ScheduleIcon />}
                label={formatScanFrequency(plan.scan_frequency)}
                size="small"
                variant="outlined"
                color={
                  plan.scan_frequency === 'every_6_hours'
                    ? 'success'
                    : plan.scan_frequency === 'twice_daily'
                    ? 'warning'
                    : 'default'
                }
              />
              <Chip
                label={`${plan.trial_days ?? 14}-day trial`}
                size="small"
                variant="outlined"
              />
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Features */}
        <Accordion disableGutters elevation={0} sx={{ border: 'none', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 36 }}>
            <Typography variant="overline" color="text.secondary">
              Features ({plan.features.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            {editing ? (
              <FeatureEditor
                features={draft.features || []}
                onChange={f => set('features', f)}
              />
            ) : (
              <List dense disablePadding>
                {plan.features.map((f, i) => (
                  <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                    <CheckIcon sx={{ fontSize: 14, color: 'success.main', mr: 1 }} />
                    <ListItemText
                      primary={f}
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Updated timestamp */}
        {plan.updated_at && (
          <Typography variant="caption" color="text.disabled" display="block" mt={1}>
            Last updated: {new Date(plan.updated_at).toLocaleString()}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PlanManagement() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const canEditPrices = isSuperAdmin;

  const [plans, setPlans] = useState<PlanConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const [globalDiscountDialog, setGlobalDiscountDialog] = useState(false);
  const [newDiscountPct, setNewDiscountPct] = useState<number>(20);
  const [savingDiscount, setSavingDiscount] = useState(false);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/plans/admin/all`);
      setPlans(data);
    } catch {
      setToast({ msg: 'Failed to load plans', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const savePlan = async (planKey: string, data: EditState) => {
    const endpoint = canEditPrices
      ? `${API_BASE}/plans/superadmin/${planKey}`
      : `${API_BASE}/plans/admin/${planKey}`;
    try {
      await axios.patch(endpoint, data);
      await fetchPlans();
      setToast({ msg: `Plan "${planKey}" saved successfully`, severity: 'success' });
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Failed to save plan';
      setToast({ msg, severity: 'error' });
      throw err;
    }
  };

  const toggleActive = async (planKey: string, active: boolean) => {
    const endpoint = canEditPrices
      ? `${API_BASE}/plans/superadmin/${planKey}`
      : `${API_BASE}/plans/admin/${planKey}`;
    try {
      await axios.patch(endpoint, { is_active: active });
      await fetchPlans();
      setToast({ msg: `Plan ${active ? 'activated' : 'deactivated'}`, severity: 'success' });
    } catch {
      setToast({ msg: 'Failed to update plan status', severity: 'error' });
    }
  };

  const applyGlobalDiscount = async () => {
    setSavingDiscount(true);
    try {
      await axios.post(`${API_BASE}/plans/superadmin/discount`, {
        annual_discount_pct: newDiscountPct,
      });
      await fetchPlans();
      setGlobalDiscountDialog(false);
      setToast({ msg: `Annual discount set to ${newDiscountPct}% on all plans`, severity: 'success' });
    } catch {
      setToast({ msg: 'Failed to update discount', severity: 'error' });
    } finally {
      setSavingDiscount(false);
    }
  };

  // Summary stats
  const activePlans = plans.filter(p => p.is_active);
  const avgMonthly = activePlans.length
    ? activePlans.reduce((s, p) => s + p.price_monthly, 0) / activePlans.length
    : 0;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Billing & Plans
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isSuperAdmin
              ? 'Full control — edit prices, features, limits, Stripe IDs, and annual discount.'
              : 'Edit plan features and limits. Contact super admin to update pricing.'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh plans">
            <IconButton onClick={fetchPlans} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {isSuperAdmin && (
            <Button
              variant="outlined"
              startIcon={<DiscountIcon />}
              onClick={() => {
                setNewDiscountPct(plans[0]?.annual_discount_pct ?? 20);
                setGlobalDiscountDialog(true);
              }}
              color="warning"
            >
              Set Global Annual Discount
            </Button>
          )}
        </Box>
      </Box>

      {/* Quick stat row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Active Plans', value: activePlans.length, icon: <TrendingUpIcon />, color: 'success.main' },
          { label: 'Avg Monthly Price', value: `$${avgMonthly.toFixed(0)}`, icon: <CreditCardIcon />, color: 'primary.main' },
          {
            label: 'Annual Discount',
            value: `${plans[0]?.annual_discount_pct ?? 20}%`,
            icon: <DiscountIcon />,
            color: 'warning.main',
          },
        ].map(stat => (
          <Paper
            key={stat.label}
            variant="outlined"
            sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, flex: '0 0 auto' }}
          >
            <Box sx={{ color: stat.color }}>{stat.icon}</Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color={stat.color}>
                {stat.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stat.label}
              </Typography>
            </Box>
          </Paper>
        ))}
        {!isSuperAdmin && (
          <Alert severity="info" icon={<LockIcon />} sx={{ flex: 1 }}>
            You have <strong>admin</strong> access. Prices are read-only — only super admin can change pricing.
          </Alert>
        )}
      </Box>

      {/* Plan grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 3 }}>
          {plans
            .sort((a, b) => a.display_order - b.display_order)
            .map(plan => (
              <PlanCard
                key={plan.plan_key}
                plan={plan}
                canEditPrices={canEditPrices}
                onSave={savePlan}
                onToggleActive={toggleActive}
              />
            ))}
        </Box>
      )}

      {/* Live preview table */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Live Pricing Preview — What Partners See
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          This is exactly what appears on the public pricing page and in the partner billing section.
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Monthly</TableCell>
                <TableCell align="right">Annual (per month)</TableCell>
                <TableCell align="right">Annual Total</TableCell>
                <TableCell align="right">Annual Savings</TableCell>
                <TableCell align="center">Max Partners</TableCell>
                <TableCell align="center">Scan</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Last Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans
                .sort((a, b) => a.display_order - b.display_order)
                .map(plan => (
                  <TableRow key={plan.plan_key} hover sx={{ opacity: plan.is_active ? 1 : 0.5 }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <strong>{plan.display_name}</strong>
                        {plan.is_popular && (
                          <Chip label="Popular" color="primary" size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} color="primary.main">
                        ${plan.price_monthly.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="success.main">${plan.price_annually_monthly.toFixed(2)}</Typography>
                    </TableCell>
                    <TableCell align="right">${plan.price_annually_total.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <Typography color="success.main" fontWeight={600}>
                        ${plan.annual_savings.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{plan.max_employees}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={formatScanFrequency(plan.scan_frequency)}
                        size="small"
                        color={
                          plan.scan_frequency === 'every_6_hours'
                            ? 'success'
                            : plan.scan_frequency === 'twice_daily'
                            ? 'warning'
                            : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={plan.is_active ? 'Active' : 'Inactive'}
                        color={plan.is_active ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">
                        {plan.updated_at ? new Date(plan.updated_at).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Global discount dialog */}
      <Dialog open={globalDiscountDialog} onClose={() => setGlobalDiscountDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DiscountIcon color="warning" />
          Update Annual Discount — All Plans
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will update the annual discount % on <strong>all active plans simultaneously</strong>.
            The pricing page and partner billing will reflect this immediately.
          </Alert>
          <TextField
            fullWidth
            label="Annual Discount %"
            type="number"
            value={newDiscountPct}
            onChange={e => setNewDiscountPct(parseFloat(e.target.value) || 0)}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 100, step: 1 }}
            helperText={`Partners who pay yearly will save ${newDiscountPct}% vs monthly billing`}
          />
          {plans.map(plan => (
            <Box key={plan.plan_key} sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">{plan.display_name} annual:</Typography>
              <Typography variant="body2" color="success.main" fontWeight={600}>
                ${(plan.price_monthly * (1 - newDiscountPct / 100)).toFixed(2)}/mo
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGlobalDiscountDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={applyGlobalDiscount}
            disabled={savingDiscount}
            startIcon={savingDiscount ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            Apply to All Plans
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={toast?.severity}
          onClose={() => setToast(null)}
          variant="filled"
          sx={{ minWidth: 300 }}
        >
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
